import { motion } from "framer-motion";
import { User, Users, TrendingDown } from "lucide-react";

const SCENARIOS = [
    {
        from: "Bastille",
        to: "Aéroport CDG",
        solo: 52,
        shared: 26,
        passengers: 2,
    },
    {
        from: "Châtelet",
        to: "Orly Sud",
        solo: 42,
        shared: 10.5,
        passengers: 4,
    },
    {
        from: "Gare de Lyon",
        to: "Disneyland",
        solo: 78,
        shared: 13,
        passengers: 6,
    },
];

export default function Savings() {
    return (
        <section
            id="savings"
            data-testid="savings-section"
            className="relative py-24 lg:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 -z-10 bg-surface-soft">
                <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_80%_30%,rgba(81,199,173,0.10),transparent_70%)]" />
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-2xl mb-16">
                    <p className="text-xs text-coway uppercase tracking-[0.3em] font-semibold mb-5">
                        Économies
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                        Le taxi
                        <br />
                        <span className="text-slate-400">enfin abordable.</span>
                    </h2>
                    <p className="mt-6 text-lg text-slate-600 max-w-xl">
                        À plusieurs, le tarif d'un taxi devient celui d'un
                        ticket de métro. Voici ce que vous économisez sur les
                        trajets les plus populaires.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                    {SCENARIOS.map((s, i) => {
                        const saved = s.solo - s.shared;
                        const pct = Math.round((saved / s.solo) * 100);
                        return (
                            <motion.div
                                key={s.to}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{
                                    duration: 0.6,
                                    delay: i * 0.1,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                whileHover={{ y: -6 }}
                                data-testid={`savings-card-${i + 1}`}
                                className="relative p-7 rounded-3xl bg-white border border-slate-200 hover:border-coway/40 transition-colors card-elevated"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                                            Trajet
                                        </p>
                                        <p className="font-display font-bold text-lg text-slate-900 mt-1 leading-tight">
                                            {s.from} → {s.to}
                                        </p>
                                    </div>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-coway-soft border border-coway/20 text-coway text-xs font-bold">
                                        <TrendingDown
                                            className="w-3 h-3"
                                            strokeWidth={2.4}
                                        />
                                        −{pct}%
                                    </span>
                                </div>

                                <div className="mt-6 grid grid-cols-2 gap-3">
                                    <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100">
                                        <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                                            <User
                                                className="w-3.5 h-3.5"
                                                strokeWidth={1.8}
                                            />
                                            Seul
                                        </div>
                                        <p className="mt-3 font-display font-black text-2xl text-slate-400 line-through decoration-[1.5px]">
                                            {s.solo.toFixed(2)}€
                                        </p>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-coway-soft border border-coway/20">
                                        <div className="flex items-center gap-1.5 text-coway text-xs font-semibold">
                                            <Users
                                                className="w-3.5 h-3.5"
                                                strokeWidth={2}
                                            />
                                            À {s.passengers}
                                        </div>
                                        <p className="mt-3 font-display font-black text-2xl text-slate-900">
                                            {s.shared.toFixed(2)}€
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between">
                                    <span className="text-xs text-slate-500">
                                        Vous économisez
                                    </span>
                                    <span className="font-display font-black text-coway text-xl">
                                        {saved.toFixed(2)}€
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="mt-10 text-sm text-slate-500 text-center max-w-2xl mx-auto"
                >
                    Estimations indicatives basées sur des tarifs moyens
                    constatés. Le partage des frais reste libre entre passagers.
                </motion.p>
            </div>
        </section>
    );
}
